import { ReactNode, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import {
	claimActiveTooltip,
	releaseActiveTooltip,
} from '@/lib/components/Tooltip/utils'

const EDIT_MODE_DELAY = 1000

interface CellTooltipProps {
	children: ReactNode
	customContent?: ReactNode
	isEditMode?: boolean
	isMenuOpen?: boolean
	title?: string | null
}

export const CellTooltip = ({
	children,
	title,
	isMenuOpen = false,
	customContent,
	isEditMode = false,
}: CellTooltipProps) => {
	const delay = isEditMode ? EDIT_MODE_DELAY : 0
	const [tooltipState, setTooltipState] = useState({
		show: false,
		position: { top: 0, left: 0 },
	})
	const [isArmed, setIsArmed] = useState(false)
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const isHoveredRef = useRef(false)
	const isMenuOpenRef = useRef(isMenuOpen)
	const triggerRef = useRef<HTMLDivElement | null>(null)
	const hideRef = useRef<() => void>(() => undefined)
	const tooltipInstanceId = useId()

	isMenuOpenRef.current = isMenuOpen

	const clearShowTimeout = () => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current)
			timeoutRef.current = null
		}
	}

	const stableHide = useRef(() => {
		hideRef.current()
	}).current

	hideRef.current = () => {
		clearShowTimeout()
		isHoveredRef.current = false
		setIsArmed(false)
		releaseActiveTooltip(stableHide)
		setTooltipState((prev) =>
			prev.show ? { ...prev, show: false } : prev,
		)
	}

	const showTooltipAt = (position: { left: number; top: number }) => {
		if (!isHoveredRef.current || isMenuOpenRef.current) return
		claimActiveTooltip(stableHide)
		setTooltipState({ show: true, position })
	}

	useEffect(() => {
		return () => {
			stableHide()
		}
	}, [stableHide])

	useEffect(() => {
		if (isMenuOpen) {
			stableHide()
		}
	}, [isMenuOpen, stableHide])

	useEffect(() => {
		if (!isArmed && !tooltipState.show) return

		const isPointerOverTrigger = (x: number, y: number) => {
			const el = triggerRef.current
			if (!el) return false
			const rect = el.getBoundingClientRect()
			return (
				x >= rect.left &&
				x <= rect.right &&
				y >= rect.top &&
				y <= rect.bottom
			)
		}

		const handlePointerMove = (event: PointerEvent) => {
			if (!isPointerOverTrigger(event.clientX, event.clientY)) {
				stableHide()
			}
		}

		const handleScrollOrResize = () => {
			stableHide()
		}

		document.addEventListener('pointermove', handlePointerMove, true)
		window.addEventListener('scroll', handleScrollOrResize, true)
		window.addEventListener('resize', handleScrollOrResize)

		return () => {
			document.removeEventListener('pointermove', handlePointerMove, true)
			window.removeEventListener('scroll', handleScrollOrResize, true)
			window.removeEventListener('resize', handleScrollOrResize)
		}
	}, [isArmed, tooltipState.show, stableHide])

	const handleMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
		const hasContent = title || customContent
		if (!hasContent || isMenuOpen) return

		isHoveredRef.current = true
		setIsArmed(true)
		const rect = event.currentTarget.getBoundingClientRect()
		const position = {
			top: rect.bottom + 8,
			left: rect.left,
		}

		clearShowTimeout()

		if (delay > 0) {
			timeoutRef.current = setTimeout(() => {
				timeoutRef.current = null
				showTooltipAt(position)
			}, delay)
		} else {
			showTooltipAt(position)
		}
	}

	const handleMouseLeave = () => {
		stableHide()
	}

	const showTooltip = tooltipState.show && !isMenuOpen
	const hasCustomContent = !!customContent
	const hasTitle = !!title

	return (
		<div className="w-full">
			<div
				ref={triggerRef}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
			>
				{children}
			</div>
			{createPortal(
				showTooltip && (hasCustomContent || hasTitle) ? (
					<div
						id={`cell-tooltip-${tooltipInstanceId}`}
						role="tooltip"
						aria-hidden
						className="pointer-events-none fixed px-3 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg shadow-lg z-[9999] dark:bg-gray-700 break-all w-fit max-w-[calc(100vw-10px)] mr-2"
						style={{
							top: tooltipState.position.top,
							left: tooltipState.position.left,
						}}
					>
						{customContent || title}
						<div className="pointer-events-none absolute -top-1 left-4 w-2 h-2 bg-gray-900 dark:bg-gray-700 transform rotate-45" />
					</div>
				) : null,
				document.body,
			)}
		</div>
	)
}
